import {
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Box,
  Text,
  HStack,
  TextProps,
} from '@chakra-ui/react';
import { TbInfoCircle } from 'react-icons/tb';
import { HabityTooltip } from '../tooltips';
import { useScroll } from '~/hooks/useScroll';

type AccordionTabItemProps = {
  title: string;
  subtitle?: string;
  hideArrow?: boolean;
  tooltip?: string;
  titleProps?: TODO;
  itemProps?: TODO;
  panelProps?: TODO;
  children: React.ReactNode;
  variant?: 'default' | 'profile';
  hasUnderline?: boolean;
};

interface TabTitleProps extends TextProps {
  isExpanded: boolean
}

function TabTitle({ children, isExpanded, ...props }: TabTitleProps) {
  return (
    <Text
      fontWeight={isExpanded ? 'semibold' : 'medium'}
      color="black"
      size="md"
      {...props}
    >
      {children}
    </Text>
  );
};


export function AccordionTabItem({
  title,
  subtitle,
  hideArrow,
  tooltip,
  titleProps,
  itemProps,
  panelProps,
  children,
  variant = 'default',
  hasUnderline = true,
}: AccordionTabItemProps) {
  const { isScrolling } = useScroll();

  return (
    <AccordionItem
      overflow="visible"
      borderTopWidth="0"
      borderBottomWidth={hasUnderline ? '1px' : '0px'}
      borderBottomColor={hasUnderline ? 'blackAlpha.500' : 'transparent'}
      py={variant === 'profile' ? 3 : 1}
      maxW="calc(100vw - 32px)" // Prevent it from growing past screen edge on phone
      overflowX="hidden"
      {...itemProps}
    >
      {({ isExpanded }) => (
        <>
          <AccordionButton
            role="group"
            py="4"
            px="0"
            pointerEvents={isScrolling ? 'none' : 'auto'}
            cursor={isScrolling ? 'auto' : 'pointer'}
            transition="opacity 0.2s"
            _hover={{
              backgroundColor: 'transparent',
            }}
          >
            {/* Label */}
            <Box as="span" flex="1" textAlign="left">
              <HStack spacing="3" alignItems="baseline">
                {/* Title */}
                {tooltip ? (
                  <HabityTooltip maxW="240" label={tooltip}>
                    <HStack spacing="1">
                      <TabTitle
                        isExpanded={isExpanded}
                        _groupHover={{ textDecoration: 'underline' }}
                        {...titleProps}
                      >
                        {title}
                      </TabTitle>
                      <TbInfoCircle />
                    </HStack>
                  </HabityTooltip>
                ) : (
                  <TabTitle
                    isExpanded={isExpanded}
                    _groupHover={{ textDecoration: 'underline' }}
                    {...titleProps}
                  >
                    {title}
                  </TabTitle>
                )}

                {/* Subtitle */}
                {subtitle && (
                  <Text size="sm" color="blackAlpha.700">
                    {subtitle}
                  </Text>
                )}
              </HStack>
            </Box>

            {!hideArrow && (
              <AccordionIcon
                color="blackAlpha.500"
                boxSize="24px"
              />
            )}
          </AccordionButton>

          <AccordionPanel py="0" px="0" overflow="visible" {...panelProps}>
            {children}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};
